import React, { useEffect, lazy, Suspense } from 'react';
import { Route, Switch, Redirect, useLocation } from 'react-router';
import * as Sentry from '@sentry/react';
import { useActiveEntity, useAnyProviderLoggedIn } from '#modules/api/User';
import { sentryDSN } from '#config/definitions';
import { clarityIdentify } from '#modules/utils/analytics';
import DesktopTitlebar from '#components/Layouts/Site/DesktopTitlebar/DesktopTitlebar';
import LogInPage from '#components/Pages/Auth/LogIn/LogInPage';
import LogOutPage from '#components/Pages/Auth/LogIn/LogOutPage';
import EmailVerificationPage from '#components/Pages/Auth/LogIn/EmailVerificationPage';
import PasswordResetPage from '#components/Pages/Auth/LogIn/PasswordResetPage';
import { useJWTRefreshCheck } from '#modules/utils/jwt';

// Lazy load pages to reduce initial bundle size
const AddAccountPage = lazy(
  () => import('#components/Pages/Auth/AddAccount/AddAccountPage'),
);
const EntityRegistrationPage = lazy(
  () => import('#components/Pages/Auth/LogIn/EntityRegistrationPage'),
);
const MFASetupPage = lazy(
  () => import('#components/Pages/Auth/LogIn/MFASetupPage'),
);
const TreasuryDetailsPage = lazy(
  () => import('#components/Pages/TreasuryDetails/TreasuryDetails'),
);
const MarketAnalysisPage = lazy(
  () => import('#components/Pages/MarketAnalysis/MarketAnalysis'),
);
const InboxPage = lazy(() => import('#components/Pages/Inbox/Inbox'));
const DashboardPage = lazy(
  () => import('#components/Pages/Dashboard/Dashboard'),
);
const BankingPage = lazy(() => import('#components/Pages/Banking/Banking'));
const TransactionsPage = lazy(
  () => import('#components/Pages/Transactions/Transactions'),
);
const PortfolioPage = lazy(
  () => import('#components/Pages/Portfolio/Portfolio'),
);
const MyPlanPage = lazy(() => import('#components/Pages/MyPlan/MyPlan'));
const DigitalCurrenciesPage = lazy(
  () => import('#components/Pages/DigitalCurrencies/DigitalCurrencies'),
);
const DocumentPage = lazy(
  () => import('#components/Pages/Documents/Documents'),
);
const StatementsPage = lazy(
  () => import('#components/Pages/Statements/Statements'),
);
const ReportsPage = lazy(() => import('#components/Pages/Reports/Reports'));
const CataloguePage = lazy(
  () => import('#components/Pages/Catalogue/Catalogue'),
);
const RedemptionPage = lazy(
  () => import('#components/Pages/Redemption/Redemption'),
);
const ProductDetailsPage = lazy(
  () => import('#components/Pages/ProductDetails/ProductDetails'),
);
const CartDetailsPage = lazy(
  () => import('#components/Pages/CartDetails/CartDetails'),
);
const CheckoutPage = lazy(() => import('#components/Pages/Checkout/Checkout'));
const DeliveryAddressesPage = lazy(
  () => import('#components/Pages/DeliveryAddresses/DeliveryAddresses'),
);
const LandingPage = lazy(() => import('#components/Pages/Landing/Landing'));
const VerificationPage = lazy(
  () => import('#components/Pages/Verification/Verification'),
);
const GiftCardRedeemPage = lazy(
  () => import('#components/Pages/GiftCardRedeem/GiftCardRedeem'),
);
const GiftCardRedeemConfirmPage = lazy(
  () => import('#components/Pages/GiftCardRedeemConfirm/GiftCardRedeemConfirm'),
);
const GiftCards = lazy(() => import('#components/Pages/GiftCards/GiftCards'));

// Simple fallback while lazy-loaded components load
function PageLoader() {
  return null;
}

function PublicRoute({ component: Component, ...rest }) {
  return (
    <Route
      {...rest}
      render={(routeProps) => {
        return <Component {...routeProps} />;
      }}
    />
  );
}

function PublicOnlyRoute({ component: Component, ...rest }) {
  const isLoggedIn = useAnyProviderLoggedIn();
  const location = useLocation();

  if (sentryDSN) {
    Sentry.setUser(null);
  }

  return (
    <Route
      {...rest}
      render={(routeProps) => {
        if (isLoggedIn) {
          let originalLocation = '/';
          if (
            location?.state?.from &&
            location?.state?.from?.pathname !== location?.pathname
          ) {
            originalLocation = location.state.from;
          }
          return <Redirect to={originalLocation} />;
        }
        return <Component {...routeProps} />;
      }}
    />
  );
}

function PrivateRoute({ component: Component, ...rest }) {
  const isLoggedIn = useAnyProviderLoggedIn();

  const entity = useActiveEntity();
  const location = useLocation();

  useJWTRefreshCheck();
  if (entity?.id) {
    const friendlyName = `${entity.id.substring(0, 8)}...${entity.id.substring(entity.id.length - 4)}`;
    clarityIdentify(entity?.id, {
      friendlyName: friendlyName,
    });
  }

  /**
   * Set Sentry user id when navigating to any private route.
   * This applies to post-login redirect and restored sessions.
   */
  if (sentryDSN && entity?.id) {
    Sentry.setUser({ id: entity.id });
  }

  return (
    <Route
      {...rest}
      render={(routeProps) =>
        isLoggedIn ? (
          <Component {...routeProps} />
        ) : (
          <Redirect
            to={{
              pathname: '/log-in',
              state: { from: location },
            }}
          />
        )
      }
    />
  );
}

const handleMouseHardwareButtons = (e) => {
  // Disable hardware mouse back/forward navigation
  if (e.button === 3 || e.button === 4) {
    e.preventDefault();
  }
};

function App() {
  useEffect(() => {
    window.addEventListener('mouseup', handleMouseHardwareButtons);
    return () => {
      window.removeEventListener('mouseup', handleMouseHardwareButtons);
    };
  }, []);

  return (
    <DesktopTitlebar>
      <Suspense fallback={<PageLoader />}>
        <Switch>
          <PublicOnlyRoute path="/log-in" component={LogInPage} />
          <PublicRoute path="/log-out" component={LogOutPage} />
          <PublicRoute path="/verify-email" component={EmailVerificationPage} />
          <PublicRoute path="/password-reset" component={PasswordResetPage} />
          <PublicRoute path="/redeem" component={GiftCardRedeemPage} />
          <PublicRoute
            path="/gift-card-redemption-confirmation"
            component={GiftCardRedeemConfirmPage}
          />
          <PrivateRoute
            path="/entity-registration"
            component={EntityRegistrationPage}
          />
          <PrivateRoute path="/mfa-setup" component={MFASetupPage} />
          <PrivateRoute path="/transactions" component={TransactionsPage} />
          <PrivateRoute path="/holdings" component={PortfolioPage} />
          <PrivateRoute path="/gift-cards" component={GiftCards} />
          <PrivateRoute path="/plan" component={MyPlanPage} />
          <PrivateRoute
            path="/digital-currencies"
            component={DigitalCurrenciesPage}
          />
          <PrivateRoute path="/banking" component={BankingPage} />
          <PrivateRoute
            path="/statements/:lockboxId"
            component={StatementsPage}
          />
          <PrivateRoute path="/statements" component={StatementsPage} />
          <PrivateRoute path="/reports" component={ReportsPage} />
          <PrivateRoute
            path="/gbuy/addresses"
            component={DeliveryAddressesPage}
          />
          <PrivateRoute path="/gbuy" component={CataloguePage} />
          <PrivateRoute path="/redemption" component={RedemptionPage} />
          <PrivateRoute
            path="/product/:productKey"
            component={ProductDetailsPage}
          />
          <PrivateRoute path="/cart/:cartId?" component={CartDetailsPage} />
          <PrivateRoute path="/checkout/:cartId" component={CheckoutPage} />
          <PrivateRoute path="/documents" component={DocumentPage} />
          <PrivateRoute
            path="/dashboard/:lockboxId"
            component={DashboardPage}
          />
          <PrivateRoute path="/dashboard" component={DashboardPage} />
          <PrivateRoute path="/landing" component={LandingPage} />
          <PrivateRoute path="/verification" component={VerificationPage} />
          <PrivateRoute path="/add-account" component={AddAccountPage} />
          <PrivateRoute
            path="/instrument/:instrumentId"
            component={TreasuryDetailsPage}
          />
          <PrivateRoute
            path="/market-analysis/:instrumentId"
            component={MarketAnalysisPage}
          />
          <PrivateRoute path="/inbox" component={InboxPage} />
          {/* <PrivateRoute
            path="/contract/:contractId"
            component={ContractEditorPage}
          /> */}
          <Route
            path="*"
            render={(props) => {
              const search = window.location?.search || props?.location?.search;
              return (
                <Redirect
                  to={{
                    pathname: '/landing',
                    search,
                  }}
                />
              );
            }}
          />
        </Switch>
      </Suspense>
    </DesktopTitlebar>
  );
}

export default App;
